import {
  Card,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableContainer,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSelectedAction,
  emptyRows,
  getComparator,
  useTable,
} from 'src/components/table';
import Scrollbar from 'src/components/scrollbar';
import { useBoolean } from 'src/hooks/use-boolean';
import { fTimestamp } from 'src/utils/format-time';
import Iconify from 'src/components/iconify';
import ReportsTableToolbar from './reports-table-toolbar';
import ReportsTableFiltersResult from './reports-table-filters-result';
import ReportsTableRow from './reports-table-row';

const REPORTS_TABLE_HEAD = [
  { id: 'updatedAt', label: 'Date' },
  { id: 'title', label: 'Title' },
  { id: 'reportStatus', label: 'Status' },
  { id: 'link', label: 'Link' },
];

function applyFilter({ inputData, comparator, filters, dateError }) {
  const { title, startDate, endDate } = filters;

  const stabilizedThis = inputData.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis.map(el => el[0]);

  if (title) {
    inputData = inputData.filter(
      report => report.title.toLowerCase().indexOf(title.toLowerCase()) !== -1,
    );
  }

  if (!dateError) {
    if (startDate && endDate) {
      endDate.setHours(23, 59, 59, 999);
      inputData = inputData.filter(
        report =>
          fTimestamp(report.createdAt) >= fTimestamp(startDate) &&
          fTimestamp(report.createdAt) <= fTimestamp(endDate),
      );
    }
  }

  return inputData;
}

const defaultFilters = {
  title: '',
  startDate: null,
  endDate: null,
};

export const ReportsTable = ({ reports }) => {
  // router
  const params = useParams();

  // hooks
  const openDateRange = useBoolean();

  const table = useTable({
    defaultRowsPerPage: 10,
    defaultOrderBy: 'updatedAt',
    order: 'desc',
  });

  // states
  const [filters, setFilters] = useState(defaultFilters);

  // variables
  const dateError =
    filters.startDate && filters.endDate
      ? filters.startDate.getTime() > filters.endDate.getTime()
      : false;

  const denseHeight = table.dense ? 56 : 76;

  const canReset =
    !!filters.title || (!!filters.startDate && !!filters.endDate);

  const dataFiltered = applyFilter({
    inputData: reports,
    comparator: getComparator(table.order, table.orderBy),
    filters,
    dateError,
  });

  const notFound = (!dataFiltered.length && canReset) || !dataFiltered.length;

  const handleFilters = useCallback(
    (name, value) => {
      table.onResetPage();
      setFilters(prevState => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table],
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  return (
    <Card>
      <Stack pt={3} px={3}>
        <Typography variant="body1" fontWeight={700}>
          Download Requests
        </Typography>
      </Stack>
      <ReportsTableToolbar
        filters={filters}
        onCloseDateRange={openDateRange.onFalse}
        onOpenDateRange={openDateRange.onTrue}
        openDateRange={openDateRange.value}
        onFilters={handleFilters}
        dateError={dateError}
      />

      {canReset && (
        <ReportsTableFiltersResult
          filters={filters}
          onFilters={handleFilters}
          openDateRange={openDateRange.value}
          onCloseDateRange={openDateRange.onFalse}
          onOpenDateRange={openDateRange.onTrue}
          onResetFilters={handleResetFilters}
          results={dataFiltered.length}
          sx={{ p: 2.5, pt: 0 }}
        />
      )}

      <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
        <TableSelectedAction
          dense={table.dense}
          numSelected={table.selected.length}
          rowCount={reports.length}
          onSelectAllRows={checked =>
            table.onSelectAllRows(
              checked,
              reports.map(row => row.id),
            )
          }
          action={
            <Stack direction="row" spacing={1}>
              <Tooltip arrow title="Download">
                <IconButton color="primary">
                  <Iconify
                    sx={{ width: 24, height: 24 }}
                    icon="material-symbols:download"
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          }
        />

        <Scrollbar>
          <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 800 }}>
            <TableHeadCustom
              order={table.order}
              orderBy={table.orderBy}
              headLabel={REPORTS_TABLE_HEAD}
              rowCount={reports.length}
              numSelected={table.selected.length}
              onSort={table.onSort}
              // onSelectAllRows={checked =>
              //   table.onSelectAllRows(
              //     checked,
              //     reports.map(row => row.id),
              //   )
              // }
            />

            <TableBody>
              {dataFiltered
                .slice(
                  table.page * table.rowsPerPage,
                  table.page * table.rowsPerPage + table.rowsPerPage,
                )
                .map(row => (
                  <ReportsTableRow
                    key={row.id}
                    row={row}
                    jobId={params.id}
                    selected={table.selected.includes(row.id)}
                    onSelectRow={() => table.onSelectRow(row.id)}
                  />
                ))}
              <TableEmptyRows
                height={denseHeight}
                emptyRows={emptyRows(
                  table.page,
                  table.rowsPerPage,
                  reports.length,
                )}
              />
              <TableNoData notFound={notFound} />
            </TableBody>
          </Table>
        </Scrollbar>
      </TableContainer>

      <TablePaginationCustom
        count={dataFiltered.length}
        page={table.page}
        rowsPerPage={table.rowsPerPage}
        onPageChange={table.onChangePage}
        onRowsPerPageChange={table.onChangeRowsPerPage}
        dense={table.dense}
        onChangeDense={table.onChangeDense}
      />
    </Card>
  );
};

ReportsTable.propTypes = {
  reports: PropTypes.array,
};
